// src/features/chat/hooks/useAssistantChat.js

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useChatFeed } from './useChatFeed';
import { useAssistantRun } from './useAssistantRun';
import { uploadChatImages, writeGroupUserMessage } from '../services/chatRepo';
import { buildAssistantReplyIndex, sortMessagesAsc } from '../models/chatModel';
import { isGroupTarget } from '../models/chatTarget';
import { buildAssistantContext, parseAssistantError } from '../services/assistantClient';

const normalizeErrorMessage = (err) => {
  if (!err) return null;
  if (typeof err === 'string') return err;
  if (err?.message) return err.message;
  try {
    return JSON.stringify(err);
  } catch (_e) {
    return String(err);
  }
};

const createClientSendId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `send_${Date.now()}_${Math.random().toString(36).slice(2)}`;
};

const buildFileSignature = (files = []) =>
  Array.from(files || [])
    .map((file) => [
      file?.name || '',
      file?.size || 0,
      file?.type || '',
      file?.lastModified || 0,
    ].join(':'))
    .join('|');

const retryablePrivateSendErrorCodes = new Set([
  '',
  'aborted',
  'cancelled',
  'deadline-exceeded',
  'unavailable',
]);

const shouldRetainPrivateRetry = (err) => {
  const code = String(err?.code || '').replace(/^functions\//, '');
  const reason = err?.details?.reason || null;
  if (reason === 'private-streaming-required') return false;
  return retryablePrivateSendErrorCodes.has(code);
};

const buildOptimisticPrivateMessage = ({
  clientSendId,
  serverMessageId = null,
  text,
  images = [],
  createdAtMs = Date.now(),
}) => ({
  id: `optimistic-private-${clientSendId}`,
  optimisticClientSendId: clientSendId,
  serverMessageId,
  isOptimistic: true,
  localStatus: 'sending',
  uiType: 'chat',
  role: 'user',
  senderId: '__current_user__',
  text,
  images,
  recordCitations: [],
  draftId: null,
  recordId: null,
  postIds: null,
  inReplyTo: null,
  timestamp: new Date(createdAtMs),
  tsMs: createdAtMs,
});

/**
 * Orchestration hook combining chat feed + assistant streaming + send.
 *
 * Key features:
 * - Unified private/group chat through ChatTarget
 * - Derived pending suppression (no imperative effects)
 * - Structured error handling for rate limits
 * - Tracks pending drafts from streaming
 *
 * @param {import('../models/chatTarget').ChatTarget} target - Chat target
 * @param {Object} [options]
 * @param {number} [options.pageSize=25] - Messages per page
 * @param {string} [options.locale] - User locale for context
 * @param {string} [options.timezone] - User timezone for context
 * @param {boolean} [options.enabled=true] - Whether to subscribe to messages
 * @returns {Object} Chat state and controls
 */
export function useAssistantChat(target, { pageSize = 25, locale, timezone, enabled = true } = {}) {
  const feed = useChatFeed(target, { pageSize, enabled });
  const assistant = useAssistantRun();
  const releaseAssistantRun = assistant.release;

  const [sending, setSending] = useState(false);
  const [sendingMessageId, setSendingMessageId] = useState(null);
  const [sendError, setSendError] = useState(null);
  const [rateLimit, setRateLimit] = useState(null);
  const [optimisticPrivateMessages, setOptimisticPrivateMessages] = useState([]);
  const sendTokenRef = useRef(0);
  const privateRetryRef = useRef(null);

  // Build context for assistant
  const context = useMemo(() => {
    return buildAssistantContext({ locale, timezone });
  }, [locale, timezone]);

  // Build lookup of which user messages have assistant replies
  // This is used for DERIVED pending suppression
  const assistantReplies = useMemo(
    () => buildAssistantReplyIndex(feed.messages),
    [feed.messages]
  );

  // DERIVED: Show pending only if no persisted reply exists
  // This eliminates the flicker from imperative clearing
  const shouldShowPending = !!(
    assistant.pending &&
    assistant.pending.inReplyTo &&
    assistant.pending.phase !== 'error' &&
    !assistantReplies.has(assistant.pending.inReplyTo)
  );

  useEffect(() => {
    const pendingReplyAnchor = assistant.pending?.inReplyTo || null;
    const releaseMessageId =
      (sendingMessageId && assistantReplies.has(sendingMessageId) && sendingMessageId) ||
      (pendingReplyAnchor && assistantReplies.has(pendingReplyAnchor) && pendingReplyAnchor) ||
      null;

    if (releaseMessageId) {
      releaseAssistantRun(releaseMessageId);
      setSending(false);
      setSendingMessageId(null);
    }
  }, [
    assistant.pending?.inReplyTo,
    assistantReplies,
    releaseAssistantRun,
    sendingMessageId,
  ]);

  // Filter out pending drafts that have been persisted
  const pendingDrafts = useMemo(() => {
    if (!assistant.pending?.pendingDrafts?.length) return [];

    return assistant.pending.pendingDrafts.filter(
      (draft) =>
        !feed.messages.some(
          (msg) => msg.uiType === 'record_draft' && msg.draftId === draft.draftId
        )
    );
  }, [assistant.pending?.pendingDrafts, feed.messages]);

  const upsertOptimisticPrivateMessage = useCallback((message) => {
    setOptimisticPrivateMessages((prev) => {
      const index = prev.findIndex((item) =>
        item.optimisticClientSendId === message.optimisticClientSendId
      );
      if (index === -1) return [...prev, message];
      const next = [...prev];
      next[index] = {
        ...prev[index],
        ...message,
      };
      return next;
    });
  }, []);

  const markOptimisticPrivateMessageCommitted = useCallback((clientSendId, commit) => {
    if (!clientSendId || !commit?.messageId) return;
    setOptimisticPrivateMessages((prev) => prev.map((message) => {
      if (message.optimisticClientSendId !== clientSendId) return message;
      const createdAtMs = Date.parse(commit.messageCreatedAt || '');
      const hasServerTime = !Number.isNaN(createdAtMs);
      return {
        ...message,
        serverMessageId: commit.messageId,
        localStatus: 'committed',
        ...(hasServerTime ? {
          timestamp: new Date(createdAtMs),
          tsMs: createdAtMs,
        } : {}),
      };
    }));
  }, []);

  const removeOptimisticPrivateMessage = useCallback((clientSendId) => {
    if (!clientSendId) return;
    setOptimisticPrivateMessages((prev) => {
      const next = prev.filter((message) =>
        message.optimisticClientSendId !== clientSendId
      );
      return next.length === prev.length ? prev : next;
    });
  }, []);

  useEffect(() => {
    if (!optimisticPrivateMessages.length) return;
    const persistedIds = new Set(feed.messages.map((message) => message.id));
    setOptimisticPrivateMessages((prev) => {
      const next = prev.filter((message) =>
        !message.serverMessageId || !persistedIds.has(message.serverMessageId)
      );
      return next.length === prev.length ? prev : next;
    });
  }, [feed.messages, optimisticPrivateMessages.length]);

  const messages = useMemo(() => {
    if (!optimisticPrivateMessages.length) return feed.messages;
    const persistedIds = new Set(feed.messages.map((message) => message.id));
    const visibleOptimistic = optimisticPrivateMessages.filter((message) =>
      !message.serverMessageId || !persistedIds.has(message.serverMessageId)
    );
    if (!visibleOptimistic.length) return feed.messages;
    return [...feed.messages, ...visibleOptimistic].sort(sortMessagesAsc);
  }, [feed.messages, optimisticPrivateMessages]);

  /**
   * Send a message and start assistant streaming.
   * @param {Object} params
   * @param {string} params.text - Message text
   * @param {Array<File>} [params.files] - Image files to upload
   * @param {string|null} [params.replyTo] - Message ID to reply to (group only)
   * @param {boolean} [params.enableAssistant=true] - Whether to trigger assistant streaming
   * @param {string} [params.primitiveSelectionId] - Optional primitive selection token
   * @returns {Promise<{ messageId: string, assistantError?: Object }>}
   */
  const send = useCallback(
    async ({
      text,
      files = [],
      replyTo = null,
      enableAssistant = true,
      primitiveSelectionId,
    }) => {
      // Validate input
      const hasText = text && text.trim();
      const hasFiles = files && files.length > 0;
      if (!hasText && !hasFiles) return;

      const sendToken = sendTokenRef.current + 1;
      sendTokenRef.current = sendToken;
      const isActiveSend = () => sendTokenRef.current === sendToken;
      const clearPrivateRetryForClient = (clientSendId) => {
        if (
          isActiveSend() ||
          privateRetryRef.current?.clientSendId === clientSendId
        ) {
          privateRetryRef.current = null;
        }
      };
      setSending(true);
      setSendingMessageId(null);
      setSendError(null);
      setRateLimit(null);
      let attemptedClientSendId = null;
      let attemptedUploadedImages = [];
      let attemptedMessageId = null;
      let attemptedMessageCreatedAtIso = null;

      try {
        const trimmedText = text.trim();
        const isGroup = isGroupTarget(target);
        const fileSignature = buildFileSignature(files);
        const retry = !isGroup &&
          privateRetryRef.current?.text === trimmedText &&
          privateRetryRef.current?.fileSignature === fileSignature &&
          privateRetryRef.current?.primitiveSelectionId ===
            (primitiveSelectionId || null)
          ? privateRetryRef.current
          : null;

        // Upload images if any. For a private retry after a transport failure,
        // reuse the uploaded URLs so the same clientSendId has the same
        // backend idempotency fingerprint.
        const uploadedImages = retry?.uploadedImages ||
          (hasFiles ? await uploadChatImages(target, files) : []);

        let messageId = retry?.messageId || null;
        let messageCreatedAtIso = retry?.messageCreatedAtIso || null;
        const clientSendId = retry?.clientSendId || createClientSendId();
        attemptedClientSendId = clientSendId;
        attemptedUploadedImages = uploadedImages;

        if (isGroup) {
          const written = await writeGroupUserMessage(target, {
            text: trimmedText,
            images: uploadedImages,
            replyTo,
          });
          messageId = written.messageId;
          messageCreatedAtIso = written.messageCreatedAtIso;
          if (isActiveSend()) {
            setSendingMessageId(messageId);
          }
        } else {
          if (isActiveSend()) {
            setSendingMessageId(clientSendId);
          }
          if (enableAssistant) {
            const retryCreatedAtMs = retry?.messageCreatedAtIso ?
              Date.parse(retry.messageCreatedAtIso) :
              NaN;
            upsertOptimisticPrivateMessage(buildOptimisticPrivateMessage({
              clientSendId,
              serverMessageId: retry?.messageId || null,
              text: trimmedText,
              images: uploadedImages,
              createdAtMs: Number.isNaN(retryCreatedAtMs) ?
                Date.now() :
                retryCreatedAtMs,
            }));
          }
        }
        attemptedMessageId = messageId;
        attemptedMessageCreatedAtIso = messageCreatedAtIso;

        // Start assistant streaming if enabled
        if (enableAssistant) {
          const final = await assistant.start({
            messageId,
            clientSendId,
            target,
            text: trimmedText,
            images: uploadedImages,
            messageCreatedAtIso,
            context,
            primitiveSelectionId,
            onMessageCommitted: isGroup ? undefined : (commit) => {
              attemptedMessageId = commit.messageId;
              attemptedMessageCreatedAtIso = commit.messageCreatedAt;
              markOptimisticPrivateMessageCommitted(clientSendId, commit);
            },
          });
          if (!isGroup && final?.messageId) {
            messageId = final.messageId;
            messageCreatedAtIso = final.messageCreatedAt;
            attemptedMessageId = messageId;
            attemptedMessageCreatedAtIso = messageCreatedAtIso;
            if (isActiveSend()) {
              setSendingMessageId(final.messageId);
            }
            markOptimisticPrivateMessageCommitted(clientSendId, {
              messageId,
              messageCreatedAt: messageCreatedAtIso,
            });
          } else if (!isGroup && !attemptedMessageId) {
            removeOptimisticPrivateMessage(clientSendId);
          }
        }

        if (!isGroup) {
          clearPrivateRetryForClient(clientSendId);
        }
        return { messageId };
      } catch (e) {
        const info = parseAssistantError(e);
        if (isActiveSend()) {
          if (info.type === 'rate_limit') {
            setRateLimit(info);
          } else {
            setSendError(info.message);
          }
        }

        if (attemptedMessageId) {
          if (!isGroupTarget(target) && attemptedClientSendId) {
            clearPrivateRetryForClient(attemptedClientSendId);
            markOptimisticPrivateMessageCommitted(attemptedClientSendId, {
              messageId: attemptedMessageId,
              messageCreatedAt: attemptedMessageCreatedAtIso,
            });
          }
          return { messageId: attemptedMessageId, assistantError: info };
        }

        if (!isGroupTarget(target) && attemptedClientSendId) {
          if (isActiveSend() && shouldRetainPrivateRetry(e)) {
            privateRetryRef.current = {
              text: text.trim(),
              fileSignature: buildFileSignature(files),
              primitiveSelectionId: primitiveSelectionId || null,
              clientSendId: attemptedClientSendId,
              uploadedImages: attemptedUploadedImages,
              messageId: null,
              messageCreatedAtIso: null,
            };
          } else if (isActiveSend()) {
            clearPrivateRetryForClient(attemptedClientSendId);
          }
          removeOptimisticPrivateMessage(attemptedClientSendId);
        }
        throw e;
      } finally {
        if (sendTokenRef.current === sendToken) {
          setSending(false);
          setSendingMessageId(null);
        }
      }
    },
    [
      assistant,
      context,
      markOptimisticPrivateMessageCommitted,
      removeOptimisticPrivateMessage,
      target,
      upsertOptimisticPrivateMessage,
    ]
  );

  // Clear error state
  const clearError = useCallback(() => {
    setSendError(null);
  }, []);

  // Clear rate limit state
  const clearRateLimit = useCallback(() => {
    setRateLimit(null);
  }, []);

  const feedError = useMemo(() => normalizeErrorMessage(feed.error), [feed.error]);
  const combinedError = sendError || feedError || null;

  return {
    // Feed state
    messages,
    status: feed.status,
    initialLoaded: feed.initialLoaded,
    hasMore: feed.hasMore,
    loadingMore: feed.loadingMore,
    loadMore: feed.loadMore,
    getMessageById: feed.getMessageById,

    // Send state
    send,
    sending,

    // Error state
    error: combinedError,
    sendError,
    feedError,
    clearError,
    rateLimit,
    clearRateLimit,

    // Pending assistant state (only shown if no persisted reply)
    pendingAssistant: shouldShowPending ? assistant.pending : null,
    pendingDrafts,
    assistantRuntime: assistant.latestRuntime || null,

    // Direct access to assistant controls
    cancelAssistant: assistant.cancel,
  };
}
